import React, {useEffect, useMemo, useRef, useState} from "react";
import {Box, CircularProgress, Skeleton, Stack} from "@mui/material";
import {useStores} from "../../../../stores";
import {useChatContext} from "../../context/ChatContext";
import type {ChatMessage} from "../../types/chatTypes";
import {Message} from "../Message";
import {ThinkingSection} from "~/pages/chat-agent/components/AgentActivity/ThinkingSection";
import {ToolCallsSection} from "~/pages/chat-agent/components/AgentActivity/ToolCallsSection";
import {AgentTurnHead} from "~/pages/chat-agent/components/AgentActivity/AgentTurnHead";
import {buildChatBlocks, withoutAnsweredTools} from "~/pages/chat-agent/utils/chatBlocks";
import {useAgentMetadata} from "~/pages/chat-agent/hooks/useAgentMetadata";
import {CHAT_CONFIG} from "~/pages/chat-agent/services/ChatConfiguration";
import {ChatInput, type ChatLexicalInputHandle} from "~/pages/chat-agent/components/ChatInput/ChatInput";
import {ChatGreeting} from "~/pages/chat-agent/components/ChatInput/ChatGreeting";
import {DRAWER_HEADER_HEIGHT_LG} from "~/components/main/drawer/DrawerConstants.ts";
import {InitiativeDraftCard} from "~/sit/chat/InitiativeDraftCard";
import {placeInitiativeDrafts} from "~/sit/chat/placeInitiativeDrafts";

const DEFAULT_PLACEHOLDER_HEIGHT = 80;
const EMPTY_MESSAGES: ChatMessage[] = [];

type Props = {
    /**
     * Side panels (Dashboards' Atlas panel): the composer stays pinned to the bottom from the first
     * render, so it doesn't jump there on the first message, and the greeting is centred above it.
     */
    docked?: boolean;
};

// Room the pinned composer takes at the bottom (its ~114px box + the 36px end offset): the docked
// greeting centres in the space above it rather than in the whole panel.
const DOCKED_COMPOSER_SPACE = 150;

export const ChatArea: React.FC<Props> = ({docked = false}) => {
    const {bagStore} = useStores();
    const {
        currentSession,
        hasReceivedFinalMessage,
        isRestoringSession,
        isWaiting,
        sendMessage,
        stopResponse,
    } = useChatContext();

    const {data: agentsMetadata} = useAgentMetadata();
    const appName = currentSession?.appName || CHAT_CONFIG.APP_NAME;

    const inputDisabled = isRestoringSession || isWaiting;
    const [placeholderHeight, setPlaceholderHeight] = useState<number | string>(DEFAULT_PLACEHOLDER_HEIGHT);
    const placeHolderHeightAfterSendingMessage = window.innerHeight - DRAWER_HEADER_HEIGHT_LG - 24 - 58 - 16 - 140 - 58 - 16;
    const handleSendMessage = async (value: string) => {
        setPlaceholderHeight(placeHolderHeightAfterSendingMessage);
        setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({behavior: "smooth"});
        }, 0);
        await sendMessage(value, bagStore.bagId);
    };

    const messages = currentSession?.messages ?? EMPTY_MESSAGES;
    // Initiative drafts move from their tool result onto the reply that follows (see placeInitiativeDrafts).
    const blocks = useMemo(
        () => withoutAnsweredTools(buildChatBlocks(placeInitiativeDrafts(messages), !hasReceivedFinalMessage)),
        [hasReceivedFinalMessage, messages],
    );

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<ChatLexicalInputHandle | null>(null);

    useEffect(() => {
        if(currentSession?.id){
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setPlaceholderHeight(DEFAULT_PLACEHOLDER_HEIGHT);
        }
        messagesEndRef.current?.scrollIntoView({behavior: "smooth"});
    }, [currentSession?.id]);

    // Focus input when appropriate
    useEffect(() => {
        // Focus on initial load or when switching to empty session
        if (!currentSession?.messages.length) {
            inputRef.current?.focus();
        }
        // Focus when receiving final message and not waiting for response
        else if (hasReceivedFinalMessage) {
            inputRef.current?.focus();
        }
    }, [hasReceivedFinalMessage, currentSession?.messages.length]);


    useEffect(() => {
        if (!hasReceivedFinalMessage) {
            if (placeholderHeight === DEFAULT_PLACEHOLDER_HEIGHT) {
                // eslint-disable-next-line react-hooks/set-state-in-effect
                setPlaceholderHeight(placeHolderHeightAfterSendingMessage);
            }
            requestAnimationFrame(() => requestAnimationFrame(() => {
                containerRef.current?.scrollTo({top: containerRef.current?.scrollHeight, behavior: "smooth"});
            }));
        }
    }, [hasReceivedFinalMessage]);

    // const containerHeight = useRef<null | number>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    return (
        <Stack
            sx={{
                flexGrow: 1,
                minHeight: 0,
                minWidth: 0,
                position: "relative",
                width: "100%",
                height: "100%",
                overflow: "auto",
            }}
            justifyContent={
                currentSession?.messages.length ? "space-between" : "center"
            }
            className="chat-area chat-area-container"
        >
            {docked && !messages.length && !isRestoringSession && (
                <Box
                    sx={{
                        position: "absolute",
                        inset: `0 0 ${DOCKED_COMPOSER_SPACE}px 0`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        px: 3,
                        pointerEvents: "none",
                    }}
                >
                    <ChatGreeting/>
                </Box>
            )}
            <Box className="chat-area-messages" sx={{maxHeight: "calc(100vh - 170px)", overflowY: "auto"}}>
                <Stack
                    sx={{
                        maxWidth: "90%",
                        "@media (min-width: 1500px)": {maxWidth: "75%"},
                        margin: "0 auto",
                    }}
                    ref={containerRef}
                >
                    {isRestoringSession ? (
                        <Stack gap={4} height={'calc(100vh - 170px)'}>
                            <Skeleton
                                width={150}
                                height={50}
                                style={{alignSelf: "flex-end"}}
                            />
                            <Stack gap={1}>
                                <Skeleton width={'98%'}/>
                                <Skeleton width={'98%'}/>
                                <Skeleton width={'98%'}/>
                                <Skeleton width={'98%'}/>
                                <Skeleton width={'85%'}/>
                            </Stack>
                            <Skeleton
                                width={250}
                                height={50}
                                style={{alignSelf: "flex-end"}}
                            />
                            <Stack gap={1}>
                                <Skeleton width={'98%'}/>
                                <Skeleton width={'98%'}/>
                                <Skeleton width={'98%'}/>
                                <Skeleton width={'98%'}/>
                                <Skeleton width={'85%'}/>
                            </Stack>

                        </Stack>
                    ) : (
                        <Box className="messages-container">
                            {!currentSession && isRestoringSession && (
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        py: 2,
                                    }}
                                >
                                    <CircularProgress size={20}/>
                                </Box>
                            )}
                            {blocks.map((block) => {
                                switch (block.kind) {
                                    case "head":
                                        return (
                                            <AgentTurnHead
                                                key={block.key}
                                                appName={appName}
                                                agents={agentsMetadata}
                                                question={block.question}
                                                replies={block.replies}
                                                isActive={block.isActive}
                                            />
                                        );
                                    case "thought":
                                        return (
                                            <ThinkingSection
                                                key={`thought-${block.message.id}`}
                                                content={block.message.content}
                                                isActive={block.isActive}
                                            />
                                        );
                                    case "tools":
                                        return (
                                            <React.Fragment key={`tools-${block.key}`}>
                                                <ToolCallsSection
                                                    calls={block.calls}
                                                    isActive={block.isActive}
                                                    followedByText={block.followedByText}
                                                    handoffs={block.handoffs}
                                                    agents={agentsMetadata}
                                                />
                                                {/* A draft no reply has taken over yet stays under its tool call. */}
                                                {block.calls.map(call => call.initiativeProposal && (
                                                    <InitiativeDraftCard
                                                        key={`draft-${call.id}`}
                                                        proposal={call.initiativeProposal}
                                                        messageId={call.id}
                                                        superseded={call.initiativeSuperseded}
                                                    />
                                                ))}
                                            </React.Fragment>
                                        );
                                    case "message":
                                        return <Message key={`message-${block.message.id}`} message={block.message}/>;
                                }
                            })}
                            <div ref={messagesEndRef} style={{}}/>
                            <Box
                                // className={!hasReceivedFinalMessage ? 'placeholder-height' : ''}
                                sx={{height: placeholderHeight,
                                //  transition: hasReceivedFinalMessage ? 'height 2s' : 'none'
                                }}
                            />
                        </Box>
                    )}
                </Stack>
            </Box>


            <ChatInput
                inputRef={inputRef}
                conversationStarted={Boolean(currentSession?.messages.length) || isRestoringSession}
                defaultPosition={docked ? "end" : undefined}
                showGreeting={!docked}
                disabled={inputDisabled}
                onSubmit={handleSendMessage}
                working={isWaiting}
                onStop={stopResponse}
            />
        </Stack>
    );
};
